import React, { useState, useRef, useEffect } from "react";
import {
  Play, ArrowRight, ShieldCheck, Brain, Layers, Database, Gauge,
  Award, Building2, Clock, Sparkles, Link2, Check, Mail, ChevronRight,
} from "lucide-react";
import { publicOrigin } from "@/lib/publicUrl";

const BG = "hsl(244,47%,6%)";
const SURFACE = "hsl(244,30%,11%)";
const ACCENT = "hsl(244,76%,64%)";

const HERO_IMG = "https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SEC_IMG = "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VIDEOS = [
  { key: "live", label: "Live Care Manager Workflow", src: "/media/gomco-live-demo.mp4", tag: "REAL SCREEN RECORDING",
    desc: "Maya, a care manager, shares her screen and works three real tasks live — triaging her population, opening a Member 360, and generating an AI clinical snapshot on the spot. Not slides — the actual platform." },
  { key: "tour", label: "Platform Tour", src: "/media/gomco-tour.mp4", tag: "60-SEC WALKTHROUGH",
    desc: "A guided tour of the full platform — command center, Member 360, AI, billing, and SLAs." },
  { key: "why", label: "Why Go MCO", src: "/media/gomco-why.mp4", tag: "THE FOUNDER ADVANTAGE",
    desc: "Built from inside NC managed care — the Eagle Healthcare founder story and why new is better." },
  { key: "ai", label: "AI Under Human Oversight", src: "/media/gomco-ai.mp4", tag: "RESPONSIBLE AI",
    desc: "How supervised AI does the busywork while licensed humans own every clinical decision." },
];

const VALUES = [
  { icon: Layers, k: "01 / ONE PLATFORM", t: "Everything on one data model", d: "Care management, UM, QM/HEDIS, contact center, portals, and end-to-end 837/835 billing — no bolt-ons.", span: "lg:col-span-7" },
  { icon: Brain, k: "02 / AI-NATIVE", t: "Configurable AI, woven in", d: "Summaries, care plans, and letters drafted in seconds — under licensed human oversight.", span: "lg:col-span-5" },
  { icon: Database, k: "03 / DATA OWNERSHIP", t: "Your data, no lock-in", d: "Full access via a documented, versioned API. You own everything submitted and generated.", span: "lg:col-span-5" },
  { icon: Gauge, k: "04 / ACCOUNTABLE", t: "SLAs with live penalties", d: "Contractual targets tracked on a dashboard that auto-calculates fee reductions on a miss.", span: "lg:col-span-7" },
];

const STEPS = [
  { n: "01", t: "Discovery & fit", d: "Map plans, populations, and security. Book a demo against your own scripts." },
  { n: "02", t: "Configure, not code", d: "85–90% of changes are configuration — your setup carries through every upgrade." },
  { n: "03", t: "Phased go-live", d: "Core live by Feb 2027, then UM/QM + billing, then AI + interoperability." },
  { n: "04", t: "Hypercare & scale", d: "Live SLA monitoring and a delivery bench that scales as membership grows." },
];

const MCOS = ["Alliance Health", "Trillium Health Resources", "Partners Health Mgmt", "Vaya Health"];
const CERTS = ["HIPAA-aligned", "SOC 2 (in progress)", "NIST 800-53 Rev 5", "NC Security Manual", "U.S.-Onshore · No Offshore PHI"];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`relative w-full px-6 sm:px-10 lg:px-20 ${className}`}>{children}</section>;
}

function HeroPreview() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <video autoPlay controls playsInline className="w-full rounded-xl bg-black aspect-video" data-testid="hero-video">
        <source src="/media/gomco-live-demo.mp4" type="video/mp4" />
      </video>
    );
  }
  return (
    <button onClick={() => setPlaying(true)} data-testid="hero-play"
      className="group relative block w-full overflow-hidden rounded-xl bg-black aspect-video">
      <video autoPlay muted loop playsInline poster="/media/gomco-live-poster.jpg"
        className="w-full h-full object-cover pointer-events-none" data-testid="hero-preview">
        <source src="/media/gomco-live-preview.mp4" type="video/mp4" />
      </video>
      <span className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,9,35,0.28)" }}>
        <span className="h-16 w-16 rounded-full flex items-center justify-center shadow-xl transition-transform group-hover:scale-110"
          style={{ background: ACCENT }}>
          <Play className="h-7 w-7 text-white ml-0.5" fill="white" />
        </span>
      </span>
      <span className="absolute bottom-3 left-3 rounded bg-black/50 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/85 backdrop-blur">
        Live workflow · tap for sound
      </span>
    </button>
  );
}

function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | done
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setState("sending");
    try {
      await fetch(`${BACKEND}/api/public/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
    } catch { /* still let them download the packet even if lead capture hiccups */ }
    try {
      const res = await fetch(`${BACKEND}/api/public/packet.zip`);
      if (!res.ok) throw new Error("packet fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "GoMCO-Overview-Packet.zip";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { /* fallback link is shown in the success state */ }
    setState("done");
  };

  if (state === "done") {
    return (
      <div className="mt-6 rounded-xl border border-white/10 p-6 text-center" data-testid="lead-success" style={{ background: "hsl(244,30%,14%)" }}>
        <Check className="h-8 w-8 mx-auto mb-2" style={{ color: ACCENT }} />
        <p className="font-display font-semibold text-lg">Your packet is downloading.</p>
        <p className="text-sm text-white/60 mt-1">Thanks — Omeze will follow up personally.</p>
        <a href={`${publicOrigin()}/api/public/packet.zip`} download data-testid="lead-download-again"
          className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-foreground transition-transform hover:scale-[1.03]"
          style={{ background: ACCENT }}>
          <ArrowRight className="h-4 w-4" /> Download didn't start? Get it here
        </a>
      </div>
    );
  }
  const inputCls = "w-full rounded-lg border border-white/15 bg-black/30 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition-colors";
  return (
    <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="lead-form">
      <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="lead-name" required />
      <input className={inputCls} placeholder="Work email *" type="email" value={form.email} onChange={set("email")} data-testid="lead-email" required />
      <input className={`${inputCls} sm:col-span-2`} placeholder="Organization (MCO / health plan)" value={form.org} onChange={set("org")} data-testid="lead-org" />
      <textarea className={`${inputCls} sm:col-span-2`} rows={3} placeholder="What are you looking to solve? (optional)" value={form.message} onChange={set("message")} data-testid="lead-message" />
      <button type="submit" disabled={state === "sending"} data-testid="lead-submit"
        className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold text-foreground transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
        style={{ background: ACCENT }}>
        {state === "sending" ? "Sending…" : <>Get the packet <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function Overline({ children }) {
  return <p className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: ACCENT }}>{children}</p>;
}

export default function Showcase() {
  const [active, setActive] = useState("live");
  const videoRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const current = VIDEOS.find((v) => v.key === active);
  const shareUrl = `${publicOrigin()}/showcase`;

  useEffect(() => { if (videoRef.current) videoRef.current.load(); }, [active]);

  const copyLink = () => {
    try { navigator.clipboard?.writeText(shareUrl)?.catch(() => {}); } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen text-white" style={{ background: BG, fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="showcase-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10" style={{ background: "hsla(163,30%,4%,0.65)", backdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-10 lg:px-20 h-16">
          <a href="#top" className="flex items-center gap-2.5" data-testid="showcase-logo">
            <img src="/brand/gomco-icon-dark.png" alt="Go MCO" className="h-8 w-8 object-contain rounded-md" />
            <span className="font-display font-extrabold tracking-tight text-lg">Go MCO</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            {[["Videos", "#videos"], ["Why", "#why"], ["Credibility", "#credibility"], ["Security", "#security"]].map(([l, h]) => (
              <a key={h} href={h} className="hover:text-white transition-colors" data-testid={`nav-${l.toLowerCase()}`}>{l}</a>
            ))}
          </nav>
          <a href="mailto:omeze.nwankwo@gomco.ai?subject=Go MCO%20—%20Request%20the%20full%20packet"
             className="rounded-full px-4 py-2 text-sm font-semibold text-foreground transition-transform hover:scale-[1.03] active:scale-95"
             style={{ background: ACCENT }} data-testid="header-cta">Request the packet</a>
        </div>
      </header>

      {/* Hero */}
      <Section id="top" className="pt-20 pb-24 lg:pt-28 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-25" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(60% 50% at 70% 30%, ${ACCENT}22, transparent 70%)` }} />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 vc-fade-up">
            <Overline>AI-native clinical platform for MCOs</Overline>
            <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95]">
              Run your entire<br />clinical operation<br /><span style={{ color: ACCENT }}>on one platform.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Care management, authorizations, quality, billing, and member engagement — with AI built in under
              licensed human oversight, and security engineered to withstand audit. Watch a real care-manager workflow — live.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#videos" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-foreground transition-transform hover:scale-[1.03] active:scale-95"
                 style={{ background: ACCENT }} data-testid="hero-watch">
                <Play className="h-4 w-4" /> Watch a real 80-second workflow
              </a>
              <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border border-white/20 hover:border-white/40 transition-colors" data-testid="hero-copy-link">
                {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Link copied" : "Copy share link"}
              </button>
            </div>
          </div>
          <div className="lg:col-span-6 vc-fade-up">
            <div className="rounded-2xl border border-white/10 p-2 shadow-2xl" style={{ background: SURFACE, boxShadow: `0 40px 120px -30px ${ACCENT}40` }}>
              <HeroPreview />
            </div>
          </div>
        </div>
      </Section>

      {/* Video Gallery */}
      <Section id="videos" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Overline>Explainer videos</Overline>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3 max-w-2xl">See the concept — no documents required.</h2>
          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="rounded-2xl border border-white/10 p-2" style={{ background: SURFACE }}>
                <video ref={videoRef} key={active} controls playsInline preload="metadata" className="w-full rounded-xl bg-black aspect-video" data-testid="gallery-video">
                  <source src={current.src} type="video/mp4" />
                </video>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Overline>{current.tag}</Overline>
                <p className="text-sm text-white/60">{current.desc}</p>
              </div>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              {VIDEOS.map((v) => (
                <button key={v.key} onClick={() => setActive(v.key)} data-testid={`video-tab-${v.key}`}
                  className="text-left rounded-xl border p-4 transition-colors"
                  style={{ background: active === v.key ? "hsl(244,30%,14%)" : SURFACE, borderColor: active === v.key ? ACCENT : "rgba(255,255,255,0.08)" }}>
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}22` }}>
                      <Play className="h-4 w-4" style={{ color: ACCENT }} />
                    </span>
                    <div>
                      <p className="font-display font-semibold">{v.label}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">{v.tag}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Why Go MCO */}
      <Section id="why" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Overline>Why Go MCO</Overline>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3 max-w-2xl">One platform. Modern economics. Real accountability.</h2>
          <div className="mt-10 grid lg:grid-cols-12 gap-5">
            {VALUES.map((v) => (
              <div key={v.t} className={`rounded-2xl border border-white/10 p-8 transition-transform hover:-translate-y-1 ${v.span}`} style={{ background: SURFACE }} data-testid={`value-${v.k.slice(0,2)}`}>
                <v.icon className="h-6 w-6 mb-5" style={{ color: ACCENT }} strokeWidth={1.75} />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{v.k}</p>
                <h3 className="font-display font-semibold text-xl mt-2">{v.t}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Credibility band */}
      <Section id="credibility" className="py-24 border-t border-white/5" >
        <div className="absolute inset-0 -z-10" style={{ background: "hsl(163,35%,3%)" }} />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Overline>The Eagle Healthcare advantage</Overline>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3 leading-tight">
              Built by an operator who's<br /> lived your world for 26 years.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
              Go MCO is founded and led by <span className="text-white font-semibold">Omeze Nwankwo</span>, who is also
              <span className="text-white font-semibold"> CFO of Eagle Healthcare Services</span> — a Joint Commission–accredited
              North Carolina provider operating for 26 years and contracted with Alliance, Trillium, Partners, and Vaya. The person
              building this platform has run care from the provider side. As founder, he is your single point of accountability.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[[Award, "26 yrs", "In operation"], [ShieldCheck, "Joint Commission", "Accredited"], [Building2, "4 NC MCOs", "Under contract"], [Clock, "Feb 2027", "Phase-1 live"]].map(([I, a, b]) => (
                <div key={a} className="rounded-xl border border-white/10 p-4" style={{ background: SURFACE }}>
                  <I className="h-5 w-5 mb-2" style={{ color: ACCENT }} strokeWidth={1.75} />
                  <p className="font-display font-bold text-lg leading-tight">{a}</p>
                  <p className="text-xs text-white/50 mt-0.5">{b}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: SURFACE }}>
              <div className="p-6 border-b border-white/10">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Contracted with</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {MCOS.map((m) => (
                    <div key={m} className="rounded-lg border border-white/10 px-3 py-3 text-sm font-display font-semibold text-white/90">{m}</div>
                  ))}
                </div>
              </div>
              <div className="p-6">
                <Sparkles className="h-5 w-5 mb-2" style={{ color: ACCENT }} />
                <p className="text-sm text-white/70 leading-relaxed">
                  New by design: no legacy overhead, AI-native from day one, and de-risked with milestone-prepaid
                  implementation, penalty-backed SLAs, and source-code &amp; data escrow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* How it works */}
      <Section id="how" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Overline>How it works</Overline>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">From selection to scale.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative rounded-2xl border border-white/10 p-6" style={{ background: SURFACE }}>
                <span className="font-mono text-3xl font-bold" style={{ color: ACCENT }}>{s.n}</span>
                <h3 className="font-display font-semibold text-lg mt-3">{s.t}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{s.d}</p>
                {i < STEPS.length - 1 && <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-white/20" />}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Security trust bar */}
      <Section id="security" className="py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-20" style={{ backgroundImage: `url(${SEC_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 -z-10" style={{ background: "hsla(163,30%,4%,0.85)" }} />
        <div className="max-w-7xl mx-auto text-center sm:text-left">
          <Overline>Security &amp; compliance</Overline>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3 max-w-3xl">Enterprise-grade security, engineered for audit.</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {CERTS.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium" style={{ background: SURFACE }} data-testid={`cert-${c.slice(0,4).toLowerCase()}`}>
                <ShieldCheck className="h-4 w-4" style={{ color: ACCENT }} /> {c}
              </span>
            ))}
          </div>
          <p className="mt-6 text-white/60 max-w-2xl text-sm">Hosted on a HIPAA-eligible platform under a signed BAA (portable to AWS or your own environment), with encryption, PHI-access audit logging, MFA, and automatic logoff.</p>
          <a href={`${publicOrigin()}/api/public/security-summary.pdf`} target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border border-white/20 hover:border-white/40 transition-colors" data-testid="download-security-summary">
            <ShieldCheck className="h-4 w-4" style={{ color: ACCENT }} /> Download the Security &amp; Compliance summary (PDF)
          </a>
        </div>
      </Section>

      {/* Lead capture */}
      <Section id="request" className="py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-white/10 p-8 sm:p-10" style={{ background: SURFACE }}>
            <Overline>Get the overview packet</Overline>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-3">Send me the packet.</h2>
            <p className="mt-3 text-white/60 max-w-xl">Drop your details and download the submission-safe overview packet instantly. We'll also follow up personally.</p>
            <LeadForm />
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-28 border-t border-white/5">
        <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(50% 60% at 50% 40%, ${ACCENT}1f, transparent 70%)` }} />
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]">Let's transform care<br />management together.</h2>
          <p className="mt-6 text-lg text-white/70">Get the full packet, book a live demo, or reach the founder directly.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <a href="mailto:omeze.nwankwo@gomco.ai?subject=Go MCO%20—%20Request%20the%20full%20packet"
               className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-foreground transition-transform hover:scale-[1.03] active:scale-95"
               style={{ background: ACCENT }} data-testid="final-cta-packet">
              <Mail className="h-4 w-4" /> Request the full packet
            </a>
            <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold border border-white/20 hover:border-white/40 transition-colors" data-testid="final-copy-link">
              {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Link copied" : "Copy share link"}
            </button>
          </div>
        </div>
      </Section>

      <footer className="border-t border-white/10 px-6 sm:px-10 lg:px-20 py-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex items-center gap-2.5">
            <span className="h-5 w-5 rounded" style={{ background: ACCENT }} />
            <span className="font-display font-bold text-white">Go MCO Technologies, Inc.</span>
          </div>
          <p>Omeze Nwankwo, Founder &amp; CEO · omeze.nwankwo@gomco.ai</p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em]">HIPAA · SOC 2 · NIST 800-53</p>
        </div>
      </footer>
    </div>
  );
}
